/**
 * Workflow-type taxonomy prompt fields — keep in sync with stream YAML / backend merge shape.
 * Used by StreamConfigEditDialog (editable) and RoleConfigEditDialog resolved view (read-only).
 */
export const WORKFLOW_TAXONOMY_PROMPT_KEYS = [
  'classifierSystemPrompt',
  'contextTriageSystemPrompt',
  'plannerSystemPrompt',
  'authorSystemPrompt',
  'reviewerSystemPrompt',
  'conversationSystemPrompt',
  'lobbySystemPrompt',
  'executionSystemPrompt',
  'executionFollowupSystemPrompt',
  'quickWriteSystemPrompt',
  'ingestSystemPrompt',
  'triageFeedbackPrompt',
  'hierarchySystemPrompt',
  'publishSystemPrompt',
  'escalationPrompt',
] as const;

export type WorkflowTaxonomyPromptKey = (typeof WORKFLOW_TAXONOMY_PROMPT_KEYS)[number];

/** Read prompt text from merged workflow type `prompts` or `entityPrompts` (string or `{ text }`). */
export function workflowTypePromptText(
  prompts: Record<string, unknown> | undefined,
  key: WorkflowTaxonomyPromptKey,
): string {
  if (!prompts) return '';
  const v = prompts[key];
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v !== null && 'text' in v) {
    return String((v as { text?: string }).text ?? '');
  }
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/** Merged object holding prompt fields (resolved taxonomy may use entityPrompts). */
export function workflowTypePromptsRecord(wt: {
  prompts?: Record<string, unknown>;
  entityPrompts?: Record<string, unknown>;
}): Record<string, unknown> | undefined {
  const a = wt.entityPrompts && typeof wt.entityPrompts === 'object' ? wt.entityPrompts : undefined;
  const b = wt.prompts && typeof wt.prompts === 'object' ? wt.prompts : undefined;
  if (a && b) return { ...b, ...a };
  return a || b;
}
